import React from 'react';
import { SchematicShell, Line } from './SchematicPrimitives';

/**
 * Elevation schematic for `dorm-chair`.
 *
 * Front: sled rails at the bottom, two front uprights, two back uprights
 * reaching full height, a front cross-rail, a seat cushion rectangle, and
 * a backrest cushion rectangle.
 * Side: sled rail, front upright, back upright, side seat rail, seat
 * cushion, backrest cushion, and bolt indicators.
 *
 * Constants match dormChairRenderer.js.
 */

const IN = 0.0254;
const RAIL = 1.25 * IN;
const SEAT_CUSHION_H = 2.5 * IN;
const BACK_CUSHION_THICK = 1.5 * IN;
const SEAT_FROM_FLOOR = 18 * IN;
const BOLT_R = 0.5 * IN;

export function DormChairSchematic({
  widthMeters: width,
  heightMeters: height,
  depthMeters: depth,
  view = 'front',
  strokeColor,
  strokeWidth,
}) {
  if (view === 'left' || view === 'right') {
    return (
      <SchematicShell
        width={depth}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      >
        <g
          transform={
            view === 'right'
              ? `scale(-1, 1) translate(${-depth}, 0)`
              : undefined
          }
        >
          <ChairSide
            width={width}
            depth={depth}
            height={height}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth}
          />
        </g>
      </SchematicShell>
    );
  }

  // FRONT
  const runnerX = width / 2 - RAIL / 2;
  const seatTopY = height - SEAT_FROM_FLOOR - SEAT_CUSHION_H;
  const seatW = width - 2 * RAIL - 0.5 * IN;
  const aboveSeat = height - SEAT_FROM_FLOOR - SEAT_CUSHION_H;
  const backCushionH = aboveSeat * 0.75;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
    >
      {/* Sled rails at bottom */}
      {[-1, 1].map((side) => {
        const x = width / 2 + side * runnerX;
        return (
          <React.Fragment key={`sled-${side}`}>
            {/* Sled rail */}
            <Line
              x1={x - RAIL / 2}
              y1={height - RAIL}
              x2={x - RAIL / 2}
              y2={height}
              strokeColor={strokeColor}
              strokeWidth={strokeWidth}
            />
            <Line
              x1={x + RAIL / 2}
              y1={height - RAIL}
              x2={x + RAIL / 2}
              y2={height}
              strokeColor={strokeColor}
              strokeWidth={strokeWidth}
            />
            {/* Back upright (full height) */}
            <Line
              x1={x - RAIL / 2}
              y1={0}
              x2={x - RAIL / 2}
              y2={height - RAIL}
              strokeColor={strokeColor}
              strokeWidth={strokeWidth}
            />
            <Line
              x1={x + RAIL / 2}
              y1={0}
              x2={x + RAIL / 2}
              y2={height - RAIL}
              strokeColor={strokeColor}
              strokeWidth={strokeWidth}
            />
          </React.Fragment>
        );
      })}

      {/* Front cross-rail at seat level */}
      <Line
        x1={width / 2 - runnerX + RAIL / 2}
        y1={height - SEAT_FROM_FLOOR}
        x2={width / 2 + runnerX - RAIL / 2}
        y2={height - SEAT_FROM_FLOOR}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Seat cushion */}
      <rect
        x={(width - seatW) / 2}
        y={seatTopY}
        width={seatW}
        height={SEAT_CUSHION_H}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />

      {/* Backrest cushion */}
      <rect
        x={(width - seatW) / 2}
        y={seatTopY - aboveSeat / 2 - backCushionH / 2}
        width={seatW}
        height={backCushionH}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    </SchematicShell>
  );
}

function ChairSide({ depth, height, strokeColor, strokeWidth }) {
  const frontZ = RAIL / 2;
  const backZ = depth - RAIL / 2;
  const sledTop = height - RAIL;
  const seatRailY = height - SEAT_FROM_FLOOR;
  const seatTopY = seatRailY - SEAT_CUSHION_H;
  const seatD = depth - 3 * IN;
  const aboveSeat = height - SEAT_FROM_FLOOR - SEAT_CUSHION_H;
  const backCushionH = aboveSeat * 0.75;

  return (
    <g>
      {/* Sled rail */}
      <Line
        x1={0}
        y1={sledTop}
        x2={depth}
        y2={sledTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Front upright */}
      <Line
        x1={frontZ - RAIL / 2}
        y1={sledTop}
        x2={frontZ - RAIL / 2}
        y2={seatRailY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={frontZ + RAIL / 2}
        y1={sledTop}
        x2={frontZ + RAIL / 2}
        y2={seatRailY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Back upright (full height) */}
      <Line
        x1={backZ - RAIL / 2}
        y1={0}
        x2={backZ - RAIL / 2}
        y2={sledTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Line
        x1={backZ + RAIL / 2}
        y1={0}
        x2={backZ + RAIL / 2}
        y2={sledTop}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Side seat rail */}
      <Line
        x1={frontZ + RAIL / 2}
        y1={seatRailY}
        x2={backZ - RAIL / 2}
        y2={seatRailY}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />

      {/* Seat cushion */}
      <rect
        x={(depth - seatD) / 2}
        y={seatTopY}
        width={seatD}
        height={SEAT_CUSHION_H}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />

      {/* Backrest cushion */}
      <rect
        x={backZ - RAIL / 2 - BACK_CUSHION_THICK}
        y={seatTopY - aboveSeat / 2 - backCushionH / 2}
        width={BACK_CUSHION_THICK}
        height={backCushionH}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />

      {/* Bolt indicators */}
      <circle
        cx={backZ}
        cy={seatRailY}
        r={BOLT_R}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      <circle
        cx={backZ}
        cy={3 * IN}
        r={BOLT_R}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
    </g>
  );
}

DormChairSchematic.supportsSideViews = true;
